"use client";

import { type FormEvent, useId, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { api } from "@/trpc/react";
import { cn } from "@/lib/utils";
import { focusRingTight } from "@/lib/focus-ring";
import { trackEvent } from "@/lib/analytics";
import { EVENT } from "@/lib/analytics-events";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AUM_OPTIONS = [
  { value: "", label: "Select range" },
  { value: "<25M", label: "Under $25M" },
  { value: "25M-100M", label: "$25M - $100M" },
  { value: "100M-500M", label: "$100M - $500M" },
  { value: "500M-1B", label: "$500M - $1B" },
  { value: ">1B", label: "$1B+" },
] as const;

const MODAL_AUM_OPTIONS = [
  { value: "", label: "AUM range" },
  { value: "<100M", label: "Under $100M" },
  { value: "100M-500M", label: "$100M - $500M" },
  { value: "500M-1B", label: "$500M - $1B" },
  { value: "1B-5B", label: "$1B - $5B" },
  { value: ">5B", label: "$5B+" },
] as const;

const initialFormData = {
  fullName: "",
  firstName: "",
  lastName: "",
  role: "",
  email: "",
  firm: "",
  website: "",
  aum: "",
  techStack: "",
  advisorCount: "",
};

const MODAL_FIELD_BASE_CLASS =
  "h-13 w-full appearance-none rounded-[var(--r-input)] px-4 font-[family-name:var(--font-body)] text-[15px] font-light shadow-none transition-[background-color,border-color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50";
const MODAL_FIELD_DARK_CLASS =
  "border-[color:var(--zom-public-white-hairline)] bg-[color:color-mix(in_srgb,var(--zom-rich-black)_55%,var(--zom-deep-graphite))] text-[color:var(--zom-warm-white)] placeholder:text-[color:var(--zom-public-white-dim)] hover:border-[color:var(--zom-public-white-hairline-strong)] focus-visible:border-[color:var(--zom-public-white-subtle)] focus-visible:bg-[color:color-mix(in_srgb,var(--zom-rich-black)_55%,var(--zom-deep-graphite))] focus-visible:ring-0";
const MODAL_FIELD_LIGHT_CLASS =
  "border-[color:var(--zom-public-line-strong)] bg-[color:var(--zom-public-panel-bg)] text-[color:var(--zom-public-ink)] placeholder:text-[color:var(--zom-public-faint)] hover:border-[color:var(--zom-public-line-strong)] focus-visible:border-[color:var(--zom-accent-primary)] focus-visible:bg-[color:var(--zom-public-panel-bg)] focus-visible:ring-1 focus-visible:ring-[color:var(--zom-public-accent-ring)]";

function splitFullName(value: string) {
  const [firstName = "", ...lastNameParts] = value.trim().split(/\s+/);
  return { firstName, lastName: lastNameParts.join(" ") };
}

export type RequestDemoFormVariant = "modal" | "card";

export interface RequestDemoFormProps {
  variant: RequestDemoFormVariant;
  /** Color treatment for the compact modal form. */
  tone?: "dark" | "light";
  /** Called when user clicks "Back to sign in" (card variant only) */
  onBack?: () => void;
}

export function RequestDemoForm({
  variant,
  tone = "dark",
  onBack,
}: RequestDemoFormProps) {
  const isModal = variant === "modal";
  const isDarkModal = isModal && tone === "dark";
  const prefersReducedMotion = useReducedMotion();
  const formInstanceId = useId();
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createDemo = api.demo.createDemoRequest.useMutation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const submittedName = isModal
      ? splitFullName(formData.fullName)
      : {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        };

    if (!submittedName.firstName) {
      if (isModal) toast.error("Name is required");
      else setError("First name is required.");
      return;
    }
    if (!submittedName.lastName) {
      if (isModal) toast.error("Enter your first and last name");
      else setError("Last name is required.");
      return;
    }
    if (!formData.email.trim()) {
      if (variant === "modal") toast.error("Email is required");
      else setError("Email is required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      const msg = "Enter a valid email address.";
      if (variant === "modal") toast.error(msg);
      else setError(msg);
      return;
    }
    if (!formData.firm.trim()) {
      if (variant === "modal") toast.error("Firm name is required");
      else setError("Firm name is required.");
      return;
    }

    try {
      await createDemo.mutateAsync({
        first_name: submittedName.firstName,
        last_name: submittedName.lastName,
        role: formData.role.trim(),
        email: formData.email.trim(),
        firm_name: formData.firm.trim(),
        website: formData.website.trim() || undefined,
        assets_under_management: formData.aum.trim(),
        tech_stack: formData.techStack.trim(),
        advisor_count: formData.advisorCount.trim(),
      });
      trackEvent(EVENT.marketing_form_submitted, { form_id: "request_demo" });
      setIsSubmitted(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "We couldn't send your request. Try again.";
      if (
        errorMessage.includes("DUPLICATE_EMAIL") ||
        errorMessage.toLowerCase().includes("duplicate")
      ) {
        toast.info("Already requested", {
          description: "We have your request. We'll reply within 24 hours.",
        });
        setIsSubmitted(true);
      } else {
        setError(errorMessage);
      }
    } finally {
      // mutation isLoading handled by createDemo.isPending if needed
    }
  };

  const isSubmitting = createDemo.isPending;
  const idPrefix = `demo-${variant}-${formInstanceId}`;

  return (
    <div className={isDarkModal ? "dark" : undefined}>
      <AnimatePresence mode="wait">
        {isSubmitted ? (
          <motion.div
            key="success"
            role="status"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={
              prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }
            }
            className={
              isModal
                ? "flex flex-col items-center justify-center py-10 text-center"
                : "flex flex-col items-center py-4 text-center"
            }
          >
            <motion.div
              initial={
                isModal && !prefersReducedMotion ? { scale: 0 } : undefined
              }
              animate={
                isModal && !prefersReducedMotion ? { scale: 1 } : undefined
              }
              transition={
                isModal && !prefersReducedMotion
                  ? { type: "spring", stiffness: 200, damping: 15, delay: 0.1 }
                  : undefined
              }
              className={
                isModal
                  ? "mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--zom-gain-soft)]"
                  : "mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--zom-gain-soft)]"
              }
            >
              <svg
                className={
                  isModal
                    ? "h-8 w-8 text-[var(--zom-gain)]"
                    : "h-6 w-6 text-[var(--zom-gain)]"
                }
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <motion.path
                  initial={
                    isModal && !prefersReducedMotion
                      ? { pathLength: 0 }
                      : undefined
                  }
                  animate={
                    isModal && !prefersReducedMotion
                      ? { pathLength: 1 }
                      : undefined
                  }
                  transition={
                    isModal && !prefersReducedMotion
                      ? { duration: 0.5, delay: 0.3 }
                      : undefined
                  }
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={isModal ? 2.5 : 2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </motion.div>
            <h4
              className={
                isModal
                  ? cn(
                      "mb-2 font-[family-name:var(--font-display)] text-xl font-medium",
                      isDarkModal
                        ? "text-[color:var(--zom-public-white-strong)]"
                        : "text-[color:var(--zom-public-ink)]",
                    )
                  : "text-foreground font-medium"
              }
            >
              You&apos;re on the list
            </h4>
            <p
              className={
                isModal
                  ? cn(
                      "font-[family-name:var(--font-body)] text-sm",
                      isDarkModal
                        ? "text-[color:var(--zom-public-white-soft)]"
                        : "text-[color:var(--zom-public-muted)]",
                    )
                  : "text-muted-foreground mt-1 text-xs"
              }
            >
              We&apos;ll reply within 24 hours.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 1 }}
            exit={
              prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -10 }
            }
            onSubmit={handleSubmit}
            name="request-access"
            autoComplete="on"
            className={cn(
              isModal ? "flex flex-col gap-3.5" : "space-y-3",
              isDarkModal && "[color-scheme:dark]",
            )}
          >
            {isModal ? (
              /* Compact request-access layout shared by the overlay and contact page. */
              <>
                <div>
                  <Label htmlFor={`${idPrefix}-fullName`} className="sr-only">
                    Name
                  </Label>
                  <Input
                    id={`${idPrefix}-fullName`}
                    name="fullName"
                    type="text"
                    autoComplete="section-request-access name"
                    required
                    value={formData.fullName}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        fullName: event.target.value,
                      }))
                    }
                    placeholder="Name"
                    className={cn(
                      MODAL_FIELD_BASE_CLASS,
                      isDarkModal
                        ? MODAL_FIELD_DARK_CLASS
                        : MODAL_FIELD_LIGHT_CLASS,
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor={`${idPrefix}-role`} className="sr-only">
                    Role
                  </Label>
                  <Input
                    id={`${idPrefix}-role`}
                    name="role"
                    type="text"
                    autoComplete="section-request-access organization-title"
                    value={formData.role}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        role: event.target.value,
                      }))
                    }
                    placeholder="Role (e.g. COO, Advisor)"
                    className={cn(
                      MODAL_FIELD_BASE_CLASS,
                      isDarkModal
                        ? MODAL_FIELD_DARK_CLASS
                        : MODAL_FIELD_LIGHT_CLASS,
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor={`${idPrefix}-email`} className="sr-only">
                    Work email
                  </Label>
                  <Input
                    id={`${idPrefix}-email`}
                    name="email"
                    type="email"
                    autoComplete="section-request-access email"
                    inputMode="email"
                    required
                    value={formData.email}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        email: event.target.value,
                      }))
                    }
                    placeholder="Work email"
                    className={cn(
                      MODAL_FIELD_BASE_CLASS,
                      isDarkModal
                        ? MODAL_FIELD_DARK_CLASS
                        : MODAL_FIELD_LIGHT_CLASS,
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor={`${idPrefix}-firm`} className="sr-only">
                    Firm name
                  </Label>
                  <Input
                    id={`${idPrefix}-firm`}
                    name="firmName"
                    type="text"
                    autoComplete="section-request-access organization"
                    required
                    value={formData.firm}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        firm: event.target.value,
                      }))
                    }
                    placeholder="Firm name"
                    className={cn(
                      MODAL_FIELD_BASE_CLASS,
                      isDarkModal
                        ? MODAL_FIELD_DARK_CLASS
                        : MODAL_FIELD_LIGHT_CLASS,
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor={`${idPrefix}-aum`} className="sr-only">
                    AUM range
                  </Label>
                  <select
                    id={`${idPrefix}-aum`}
                    name="assetsUnderManagement"
                    autoComplete="off"
                    value={formData.aum}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        aum: event.target.value,
                      }))
                    }
                    className={cn(
                      MODAL_FIELD_BASE_CLASS,
                      isDarkModal
                        ? MODAL_FIELD_DARK_CLASS
                        : MODAL_FIELD_LIGHT_CLASS,
                      formData.aum
                        ? isDarkModal
                          ? "text-[color:var(--zom-warm-white)]"
                          : "text-[color:var(--zom-public-ink)]"
                        : isDarkModal
                          ? "text-[color:var(--zom-public-white-soft)]"
                          : "text-[color:var(--zom-public-faint)]",
                      isDarkModal &&
                        "[&>option]:bg-[color:var(--zom-warm-white)] [&>option]:text-[color:var(--zom-rich-black)]",
                    )}
                  >
                    {MODAL_AUM_OPTIONS.map((option) => (
                      <option
                        key={option.value || "empty"}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor={`${idPrefix}-techStack`} className="sr-only">
                    Systems you use
                  </Label>
                  <Input
                    id={`${idPrefix}-techStack`}
                    name="techStack"
                    type="text"
                    autoComplete="off"
                    value={formData.techStack}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        techStack: event.target.value,
                      }))
                    }
                    placeholder="Systems you use (CRM, custodian, portfolio)"
                    className={cn(
                      MODAL_FIELD_BASE_CLASS,
                      isDarkModal
                        ? MODAL_FIELD_DARK_CLASS
                        : MODAL_FIELD_LIGHT_CLASS,
                    )}
                  />
                </div>
                <div>
                  <Label
                    htmlFor={`${idPrefix}-advisorCount`}
                    className="sr-only"
                  >
                    Number of advisors
                  </Label>
                  <Input
                    id={`${idPrefix}-advisorCount`}
                    name="advisorCount"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    value={formData.advisorCount}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        advisorCount: event.target.value,
                      }))
                    }
                    placeholder="Number of advisors"
                    className={cn(
                      MODAL_FIELD_BASE_CLASS,
                      isDarkModal
                        ? MODAL_FIELD_DARK_CLASS
                        : MODAL_FIELD_LIGHT_CLASS,
                    )}
                  />
                </div>
                {error && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="rounded-[var(--r-input)] border border-[var(--zom-loss-border)] bg-[var(--zom-loss-soft)] p-3 font-[family-name:var(--font-body)] text-sm text-[var(--zom-loss)]"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  className="mt-1.5 h-13 w-full rounded-[var(--r-btn)] border-transparent bg-[color:var(--zom-accent-primary)] font-[family-name:var(--font-body)] text-[15px] font-normal text-[color:var(--zom-on-accent)] shadow-none hover:bg-[color:var(--zom-accent-primary-hover)] hover:text-[color:var(--zom-on-accent)]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2
                        className="animate-spin motion-reduce:animate-none"
                        aria-hidden
                      />
                      <span>Submitting</span>
                    </>
                  ) : (
                    "Request access"
                  )}
                </Button>
              </>
            ) : (
              /* Card variant: login page styles */
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label
                      htmlFor={`${idPrefix}-firstName`}
                      className="text-xs"
                    >
                      First name
                    </Label>
                    <Input
                      id={`${idPrefix}-firstName`}
                      name="firstName"
                      autoComplete="section-request-access given-name"
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      placeholder="Jane"
                      className="h-9 rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`${idPrefix}-lastName`} className="text-xs">
                      Last name
                    </Label>
                    <Input
                      id={`${idPrefix}-lastName`}
                      name="lastName"
                      autoComplete="section-request-access family-name"
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      placeholder="Smith"
                      className="h-9 rounded-xl"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${idPrefix}-role`} className="text-xs">
                    Role
                  </Label>
                  <Input
                    id={`${idPrefix}-role`}
                    name="role"
                    autoComplete="section-request-access organization-title"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    placeholder="COO, Advisor"
                    className="h-9 rounded-xl"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${idPrefix}-email`} className="text-xs">
                    Work email
                  </Label>
                  <Input
                    id={`${idPrefix}-email`}
                    name="email"
                    type="email"
                    autoComplete="section-request-access email"
                    inputMode="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="jane@yourfirm.com"
                    className="h-9 rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${idPrefix}-firm`} className="text-xs">
                    Firm name
                  </Label>
                  <Input
                    id={`${idPrefix}-firm`}
                    name="firmName"
                    autoComplete="section-request-access organization"
                    value={formData.firm}
                    onChange={(e) =>
                      setFormData({ ...formData, firm: e.target.value })
                    }
                    placeholder="Acme Advisors"
                    className="h-9 rounded-xl"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${idPrefix}-website`} className="text-xs">
                    Website
                  </Label>
                  <Input
                    id={`${idPrefix}-website`}
                    name="website"
                    type="url"
                    autoComplete="section-request-access url"
                    inputMode="url"
                    value={formData.website}
                    onChange={(e) =>
                      setFormData({ ...formData, website: e.target.value })
                    }
                    placeholder="https://yourfirm.com"
                    className="h-9 rounded-xl"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${idPrefix}-aum`} className="text-xs">
                    Assets under management
                  </Label>
                  <select
                    id={`${idPrefix}-aum`}
                    name="assetsUnderManagement"
                    autoComplete="off"
                    value={formData.aum}
                    onChange={(e) =>
                      setFormData({ ...formData, aum: e.target.value })
                    }
                    className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-9 w-full rounded-xl border px-3 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    {AUM_OPTIONS.map((opt) => (
                      <option key={opt.value || "empty"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${idPrefix}-techStack`} className="text-xs">
                    Systems you use
                  </Label>
                  <Input
                    id={`${idPrefix}-techStack`}
                    name="techStack"
                    autoComplete="off"
                    value={formData.techStack}
                    onChange={(e) =>
                      setFormData({ ...formData, techStack: e.target.value })
                    }
                    placeholder="CRM, custodian, portfolio"
                    className="h-9 rounded-xl"
                  />
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor={`${idPrefix}-advisorCount`}
                    className="text-xs"
                  >
                    Number of advisors
                  </Label>
                  <Input
                    id={`${idPrefix}-advisorCount`}
                    name="advisorCount"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    value={formData.advisorCount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        advisorCount: e.target.value,
                      })
                    }
                    placeholder="e.g. 8"
                    className="h-9 rounded-xl"
                  />
                </div>
                {error && (
                  <p role="alert" className="text-destructive text-xs">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full rounded-xl bg-[var(--zom-rich-black)] py-2 text-sm text-[color:var(--zom-warm-white)] hover:bg-[color:var(--zom-rich-black)]/90"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        aria-hidden
                        className="border-muted-foreground/40 inline-block h-4 w-4 animate-spin rounded-full border-2 border-t-current motion-reduce:animate-none"
                      />
                      Submitting…
                    </>
                  ) : (
                    "Request access"
                  )}
                </Button>
              </>
            )}
          </motion.form>
        )}
      </AnimatePresence>

      {variant === "card" && onBack && (
        <p className="text-muted-foreground mt-2 text-center text-xs">
          <button
            type="button"
            onClick={() => {
              setError(null);
              onBack();
            }}
            className={cn(
              "hover:text-foreground rounded-sm font-medium underline-offset-4 transition-colors hover:underline",
              focusRingTight,
            )}
          >
            Back to sign in
          </button>
        </p>
      )}
    </div>
  );
}
